import { CROWD_RULES } from '@shared/types';
import {
  hasHomograph,
  NOTES,
  transliterateWord,
  transliterationCandidates,
  type TransliterationCandidate,
} from '../engine';
import { crowdContested, crowdDefault, crowdReadings, ensureCrowd } from './crowd';
import { contributePick } from './contribute';

/**
 * What this writer picked last time.
 *
 * The candidate list is ranked by judgement, not by measurement — there is no
 * Nepali frequency corpus behind it, and there is no ranking that could be right
 * for everyone anyway. A poet writing about पानी and one writing पनि in every
 * second sentence want opposite defaults for the same six keystrokes.
 *
 * Remembering the choice makes the ranking a question that only has to be
 * answered once per spelling per writer. It is also the whole difference between
 * a picker that feels like an input method and one that feels like a quiz.
 *
 * Local to the browser on purpose: it is a typing habit, not content, and
 * syncing it would mean a round trip on a keystroke path that must never wait
 * for the network.
 */

const KEY = 'kaha:tl-picks';

/**
 * Enough to cover a writer's own vocabulary, bounded so the entry never grows
 * without limit. Eviction is oldest-first, which is the right order for a habit:
 * the words someone has stopped writing are the ones to forget.
 */
const MAX_PICKS = 500;

type Picks = Record<string, string>;

let cache: Picks | null = null;

function load(): Picks {
  if (cache) return cache;
  try {
    const stored = localStorage.getItem(KEY);
    cache = stored ? (JSON.parse(stored) as Picks) : {};
  } catch {
    // Private mode, a full quota, or a corrupted entry: the feature degrades to
    // "no memory", which is exactly how it behaved before anyone chose anything.
    cache = {};
  }
  return cache;
}

/** Case-insensitive: "Pani" and "pani" are the same decision. */
function normalise(roman: string): string {
  return roman.toLowerCase();
}

/** What this writer chose for `roman` before, if anything. */
export function rememberedPick(roman: string): string | null {
  return load()[normalise(roman)] ?? null;
}

/**
 * Records a choice — and forgets it again when it is the default.
 *
 * Storing "they picked what the engine already gives" would be a row that can
 * never change anything, and it would also stop a *later* improvement to the
 * engine from reaching this writer for that word.
 */
export function rememberPick(roman: string, text: string, options: { contribute?: boolean } = {}): void {
  const key = normalise(roman);
  const picks = load();

  /*
   * The same choice, offered to everyone else. Queued rather than sent: this is
   * a keystroke path, and one correction is not worth a request.
   *
   * Per call rather than by flipping the global switch, because contribution is
   * a property of the *field* in an embed: a page may carry a public comment box
   * and a private one, and turning it off for the second must not turn it off
   * for the site — or, worse, persist that to the visitor's browser.
   */
  if (options.contribute !== false) contributePick(key, text);

  if (text === transliterateWord(roman)) {
    if (!(key in picks)) return;
    delete picks[key];
  } else {
    // Re-inserted rather than updated in place, so a word used again becomes the
    // newest entry and survives eviction.
    delete picks[key];
    picks[key] = text;
  }

  const keys = Object.keys(picks);
  for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_PICKS))) delete picks[stale];

  try {
    localStorage.setItem(KEY, JSON.stringify(picks));
  } catch {
    /* Nothing to do: the choice applies to this session and is not persisted. */
  }
}

/**
 * The conversion to insert for a romanized word.
 *
 * Three layers, narrowing: what this writer chose before, what enough other
 * people chose, and what the engine works out on its own. Every place that
 * converts as you type goes through here rather than calling `transliterateWord`
 * directly, so a correction made once in a title holds in the editor and in a
 * comment box too.
 */
export function convertWord(roman: string): string {
  ensureCrowd();
  return rememberedPick(roman) ?? crowdDefault(roman) ?? transliterateWord(roman);
}

/**
 * Whether a word is worth interrupting somebody about.
 *
 * The picker offers itself after a conversion, and the question is when. "More
 * than one candidate" is not the answer: the engine generates readings along
 * mechanical axes — retroflex against dental, long against short — so 32 of 37
 * ordinary words come back with several, and a list that appeared after nearly
 * every word would be a flicker to type through rather than a help.
 *
 * Genuine ambiguity is much rarer and is two specific things. A homograph is one
 * roman spelling standing for two different Nepali words — `pani` is पानी and
 * पनि, `tara` is तारा and तर — where the writer is the only one who knows which
 * they meant. The other is a spelling people have actually disagreed about,
 * measured rather than assumed: two readings, both with real support, neither
 * winning. Everywhere else the first guess is left to stand, and Ctrl+Space or a
 * click still opens the full list for anyone who wants it.
 */
export function worthOffering(roman: string): boolean {
  ensureCrowd();
  return hasHomograph(roman) || crowdContested(roman);
}

/**
 * The candidate list, ranked for this writer.
 *
 * Their own choice first — the row the picker opens on has to be the text
 * already sitting in the document — then any reading the crowd supports, then
 * the engine's own ordering. The crowd rows are merely promoted here even when
 * they are not confident enough to have changed the default, which is the whole
 * of what the shared ranking does for an ambiguous word.
 */
export function candidatesFor(roman: string, limit = 5): TransliterationCandidate[] {
  ensureCrowd();

  const candidates = transliterationCandidates(roman, {}, limit);
  const supported = crowdReadings(roman).filter((pick) => pick.votes >= CROWD_RULES.minVoters);
  const chosen = rememberedPick(roman);

  const order = [chosen, ...supported.map((pick) => pick.text)].filter(Boolean) as string[];
  if (!order.length) return candidates;

  const known = new Map(candidates.map((candidate) => [candidate.text, candidate]));
  const promoted: TransliterationCandidate[] = [];

  for (const text of order) {
    if (promoted.some((candidate) => candidate.text === text)) continue;
    const existing = known.get(text);
    // A crowd reading the engine never thought of still belongs in the list —
    // that is precisely the case where the ranking has learnt something.
    promoted.push({ text, note: existing?.note ?? (text === chosen ? undefined : NOTES.crowd) });
    known.delete(text);
  }

  return [...promoted, ...candidates.filter((candidate) => known.has(candidate.text))].slice(0, limit);
}
